import './management-profile.scss';
import 'react-phone-input-2/lib/style.css';

import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Button,
  Grid,
  IconButton,
  Modal,
  TextField,
  Typography,
} from '@mui/material';
import profileimg from 'assets/images/iPrise/profileimg.png';
import CustomButton from 'components/CustomButton/customButton';
import { useFormik } from 'formik';
import { useStore } from 'mobx/StoreContext';
import { observer } from 'mobx-react-lite';
import ChangePassword from 'pages/common/ChangePassword';
import React, { useCallback, useEffect } from 'react';
import PhoneInput from 'react-phone-input-2';
import { formatPhoneNumber } from 'react-phone-number-input';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  useFetchList,
  useUpdateItem,
} from 'services/common/react-query-common-service';
import { useUserProfile } from 'services/profile/profileService';
import { logout, ssoLogout } from 'store/actions/UserAction';
import { useAppSelector } from 'store/hooks';
import { MASTER_ENDPOINT } from 'utils/Constant';
import { editProfileSchema } from 'utils/ValidatorSchema';

import StylishLoader from '../components/StylishLoader';

const ManagementProfile = observer(() => {
  const { profileStore } = useStore();
  const { isProfileInfo } = profileStore;
  const navigate = useNavigate();
  const dispatch: any = useDispatch();

  const { userInfo }: any = useAppSelector((store: any) => store.userLogin);

  const { data: versionHistoryResp } = useFetchList<any>(
    ['versionHistory'],
    `${MASTER_ENDPOINT.VersionHistory}?order=desc&sortby=versionId&skip=0&take=1`,
    {
      refetchOnMount: 'always',
    },
  );

  const { isProfileLoading, refetchProfile } = useUserProfile(userInfo?.id);

  const { mutateAsync: updateProfile, isPending: isUpdating } = useUpdateItem();

  const versionData = versionHistoryResp?.data?.[0];

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      email: isProfileInfo?.login?.email || '',
      firstName: isProfileInfo?.firstName || '',
      lastName: isProfileInfo?.lastName || '',
      phoneNumber: isProfileInfo?.phoneNumber || '',
    },
    onSubmit: (values) => {
      submitForm(values);
    },
    validationSchema: editProfileSchema,
  });

  // Sync version ID to MobX store
  useEffect(() => {
    if (versionData) {
      profileStore.setVersionId(versionData.versionId);
    }
  }, [versionData, profileStore]);

  const handleOpen = useCallback(() => {
    profileStore.setEditModalOpen(true);
  }, [profileStore]);

  const handleClose = useCallback(() => {
    profileStore.setEditModalOpen(false);
    profileStore.resetImageUpload();
  }, [profileStore]);

  const submitForm = useCallback(
    async (data: any) => {
      const formData = new FormData();
      formData.append('status', 'true');
      formData.append('roleId', isProfileInfo?.roleId);
      formData.append('email', data.email);
      formData.append('empCode', isProfileInfo?.empCode);
      formData.append('firstName', data.firstName);
      formData.append('organizationId', isProfileInfo?.organizationId);
      formData.append('lastName', data.lastName);

      if (data.phoneNumber) {
        const fullPhoneNumber = data.phoneNumber.replace(/[^0-9]/g, '');
        formData.append('phoneNumber', fullPhoneNumber);
        formData.append('countryCode', profileStore.countryCode || '1');
      }

      if (profileStore.selectedFile) {
        formData.append('profile', profileStore.selectedFile);
      } else if (profileStore.isProfileInfo?.imageId) {
        formData.append('imageId', profileStore.isProfileInfo?.imageId);
      }

      try {
        await updateProfile({
          data: formData,
          endpoint: `${MASTER_ENDPOINT.Admins}/profile`,
          id: profileStore.isProfileInfo?.id,
        });
        handleClose();
        refetchProfile();
      } catch (err) {
        // Error handled by hook
      }
    },
    [isProfileInfo, profileStore, handleClose, updateProfile, refetchProfile],
  );

  const logoutHandler = useCallback(async () => {
    await ssoLogout();
    dispatch(logout(true));
  }, [dispatch]);

  if (isProfileLoading) {
    return <StylishLoader />;
  }

  const currentProfile = isProfileInfo;

  return (
    <Box className="management-profile-root">
      <Box className="profile-container">
        {/* Personal Info Section */}
        <Box>
          <Typography className="section-title">Personal Info</Typography>
          <Box className="profile-card">
            <Typography className="edit-link" onClick={handleOpen}>
              Edit
            </Typography>
            <Box className="personal-info-content">
              <Box className="avatar-placeholder">
                <img
                  src={
                    isProfileInfo?.profileImage
                      ? `${process.env.REACT_APP_APIURL}${
                          MASTER_ENDPOINT.Files
                        }/${String(
                          isProfileInfo.profileImage?.id,
                        )}?userId=${String(userInfo?.id)}`
                      : profileimg
                  }
                  alt="Avatar"
                />
              </Box>
              <Box className="info-details">
                <Box className="info-row">
                  <span className="info-label">Name</span>
                  <span className="info-value">
                    {currentProfile?.firstName} {currentProfile?.lastName}
                  </span>
                </Box>
                <Box className="info-row">
                  <span className="info-label">Email</span>
                  <span className="info-value">
                    {currentProfile?.login?.email}
                  </span>
                </Box>
                <Box className="info-row">
                  <span className="info-label">Phone</span>
                  <span className="info-value">
                    {currentProfile?.phoneNumber
                      ? formatPhoneNumber('+' + currentProfile.phoneNumber)
                      : 'N/A'}
                  </span>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* More Options Section */}
        <Box>
          <Typography className="section-title">More Options</Typography>
          <Box className="options-card">
            <ul className="options-list">
              <li className="option-item">
                <ChangePassword />
              </li>
              <li className="option-item">Settings</li>
              <li className="option-item">Privacy Policy</li>
              <li className="option-item">Terms & Conditions</li>
              <li
                className="option-item logout-row"
                onClick={() => profileStore.setLogoutModalOpen(true)}>
                <Box sx={{ color: '#BF0000', cursor: 'pointer' }}>Logout</Box>
                <span
                  className="version-label"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate('/version-history');
                  }}>
                  {profileStore.versionId || 'vS1.1.1b2'}
                </span>
              </li>
            </ul>
          </Box>
        </Box>
      </Box>

      {/* Edit Profile Modal */}
      <Modal open={profileStore.isEditModalOpen} onClose={handleClose}>
        <Box className="profile-modal-content">
          <Box className="modal-header">
            <Typography variant="h6" className="modal-title">
              Edit Your Profile Details
            </Typography>
            <IconButton onClick={handleClose} className="close-icon-btn">
              <CloseIcon />
            </IconButton>
          </Box>

          <Box sx={{ mb: 4, textAlign: 'center' }}>
            <Button
              variant="text"
              component="label"
              sx={{ borderRadius: '50%', p: 0 }}>
              <Box
                component="img"
                src={
                  profileStore.localImagePreview
                    ? profileStore.localImagePreview
                    : currentProfile?.imageId
                    ? `${process.env.REACT_APP_APIURL}${
                        MASTER_ENDPOINT.Files
                      }/${String(currentProfile.imageId)}?userId=${String(
                        userInfo?.id,
                      )}`
                    : profileimg
                }
                sx={{
                  borderRadius: '50%',
                  height: 100,
                  objectFit: 'cover',
                  width: 100,
                }}
              />
              <input
                hidden
                accept="image/*"
                type="file"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    profileStore.setSelectedFile(file);
                    profileStore.setLocalImagePreview(
                      URL.createObjectURL(file),
                    );
                  }
                }}
              />
            </Button>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" className="field-label">
                First Name*
              </Typography>
              <TextField
                fullWidth
                name="firstName"
                size="small"
                value={formik.values.firstName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.firstName && Boolean(formik.errors.firstName)
                }
                helperText={
                  (formik.touched.firstName && formik.errors.firstName) as any
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" className="field-label">
                Last Name*
              </Typography>
              <TextField
                fullWidth
                name="lastName"
                size="small"
                value={formik.values.lastName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.lastName && Boolean(formik.errors.lastName)
                }
                helperText={
                  (formik.touched.lastName && formik.errors.lastName) as any
                }
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="caption" className="field-label">
                Email*
              </Typography>
              <TextField
                fullWidth
                disabled
                name="email"
                size="small"
                value={formik.values.email}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="caption" className="field-label">
                Phone Number*
              </Typography>
              <PhoneInput
                country={'us'}
                value={formik.values.phoneNumber}
                onChange={(value, data: any) => {
                  formik.setFieldValue('phoneNumber', '+' + value);
                  profileStore.setCountryCode(data?.dialCode);
                }}
                onBlur={() => formik.setFieldTouched('phoneNumber', true)}
                inputProps={{
                  name: 'phoneNumber',
                  required: true,
                }}
                containerClass="react-tel-input"
                inputClass="form-control"
              />
              {formik.touched.phoneNumber && formik.errors.phoneNumber && (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{ display: 'block', mt: 0.5 }}>
                  {formik.errors.phoneNumber as string}
                </Typography>
              )}
            </Grid>
          </Grid>

          <Box className="modal-actions">
            <Button onClick={handleClose} className="cancel-btn">
              Cancel
            </Button>
            <CustomButton
              variant="contained"
              loading={isUpdating}
              name="Save Changes"
              onPress={formik.handleSubmit}
              className="save-btn"
              isStyle={true}
              isTextTransform={false}
            />
          </Box>
        </Box>
      </Modal>

      {/* Logout Modal */}
      <Modal
        open={profileStore.isLogoutModalOpen}
        onClose={() => profileStore.setLogoutModalOpen(false)}>
        <Box className="profile-modal-content confirm-modal">
          <Box
            className="modal-header"
            sx={{ justifyContent: 'center', position: 'relative' }}>
            <Typography
              variant="h6"
              className="modal-title confirm-message"
              sx={{ textAlign: 'center', width: '100%' }}>
              Are you sure you want to Logout?
            </Typography>
            <IconButton
              onClick={() => profileStore.setLogoutModalOpen(false)}
              className="close-icon-btn"
              sx={{ position: 'absolute', right: 0 }}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Box className="modal-actions center-actions" sx={{ mt: 3 }}>
            <Button
              variant="outlined"
              onClick={() => profileStore.setLogoutModalOpen(false)}
              className="cancel-btn">
              No, Cancel
            </Button>
            <Button
              variant="contained"
              onClick={logoutHandler}
              className="confirm-destruct-btn">
              YES, Logout
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
});

export default ManagementProfile;
